import { Button, Card, Divider, Flex, Form, Modal, Radio, Select, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { BiTransfer } from 'react-icons/bi';
import { HiOutlineDuplicate } from 'react-icons/hi';
import { TASk_TABS } from '../../constants';
import { isStaff, priorityWithIcon } from '../../helper';
import useRestaurants from '../../hooks/useRestaurants';
import { getTasks, moveOrCopyTask } from '../../redux/actions/tasks';

const { Text, Title } = Typography;

const CopyDuplicateTaskModal = ({ open, onClose, task, currentRestaurantId }) => {
  const [form] = Form.useForm();
  const { restaurants } = useRestaurants();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    const res = await moveOrCopyTask(task.id, {
      target_restaurant_id: values.target_restaurant_id,
      action: values.action,
    });

    if (res?.ack) {
      getTasks({ id: currentRestaurantId, filter: TASk_TABS.PENDING });
      onClose();
    }
    setLoading(false);
  };

  const currentSite = restaurants?.find((r) => r.id === parseInt(currentRestaurantId));

  return (
    <Modal
      title="Copy or Move Task"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={520}
      centered
      styles={{
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: '#00000015',
        },
        content: {
          borderRadius: '12px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <div className="duplicate-modal-container">
        <Text type="secondary" className="task-summary-title">
          Task Summary
        </Text>
        <Card size="small" className="task-summary-card">
          <Flex vertical gap={12}>
            <Title level={5} className="task-title">
              {task?.title}
            </Title>
            <Flex gap={24} wrap="wrap">
              <div className="detail-item">
                <Text className="label">Priority</Text>
                <div style={{ scale: '0.9', transformOrigin: 'left' }}>
                  {priorityWithIcon(task?.priority)}
                </div>
              </div>
              <div className="detail-item">
                <Text className="label">Due Date</Text>
                <Text className="value">
                  {task?.due_date ? dayjs(task.due_date).format('DD/MM/YYYY HH:mm') : 'No Due Date'}
                </Text>
              </div>
              <div className="detail-item">
                <Text className="label">Current Site</Text>
                <Text className="value">{currentSite?.title || 'Unknown'}</Text>
              </div>
            </Flex>
          </Flex>
        </Card>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ action: 'duplicate' }}
        requiredMark={false}
      >
        <Form.Item
          name="target_restaurant_id"
          label={<Text className="form-label-bold">Destination Site</Text>}
          rules={[{ required: true, message: 'Please select a destination site' }]}
        >
          <Select
            showSearch
            size="large"
            placeholder="Search and select a site..."
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={restaurants
              ?.filter((r) => r.id !== parseInt(currentRestaurantId))
              ?.map((r) => ({
                label: r.title,
                value: r.id,
                disabled: isStaff() && r.allow_staff_create_task !== 1,
              }))}
          />
        </Form.Item>

        <Form.Item
          name="action"
          label={<Text className="form-label-bold">Action Type</Text>}
          rules={[{ required: true, message: 'Please select an action' }]}
        >
          <Radio.Group style={{ width: '100%' }} className="action-radio-group">
            <Flex gap={12}>
              <Radio.Button value="duplicate">
                <div className="action-content">
                  <HiOutlineDuplicate size={24} />
                  <Text className="action-title">Copy</Text>
                  <Text className="action-desc">Keep original task</Text>
                </div>
              </Radio.Button>
              <Radio.Button value="move">
                <div className="action-content">
                  <BiTransfer size={24} />
                  <Text className="action-title">Move</Text>
                  <Text className="action-desc">Archive original</Text>
                </div>
              </Radio.Button>
            </Flex>
          </Radio.Group>
        </Form.Item>

        <Divider style={{ margin: '24px 0 16px' }} />

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space size={12} className="modal-footer-buttons">
            <Button onClick={onClose} size="large" className="cancel-btn">
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              className="confirm-btn"
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CopyDuplicateTaskModal;
